import { useCallback, useMemo } from 'react';
import { FormApi, ValidationErrors } from 'final-form';
import i18n from 'i18next';

import {
	CheckinPassengerFragment,
	GdsServiceProductStatus,
	PassengerFragment,
	RegExpParameter,
	TravellerFieldEnum,
	TravellerParameterFragment
} from '@websky/graphql';

import { TravellerFields } from '../../../../TravellerForm/types';

import { clearPhoneNumber } from '../../../../PhoneInput/utils';
import { getValueByName } from '../../../../CurrentUser/utils';

import { formatPhone } from '../../../../BaseComponents/PhoneInput/utils';

import { getFormTravellerKey, SmsPopupFormValues } from '../utils';
import { getUserValue } from '../../../../utils';

export const useSmsPopupValidation = (regExp: RegExpParameter[]) => {
	const testPhone = (traveller: SmsPopupFormValues['key'], values: SmsPopupFormValues) => {
		const cleanValue = clearPhoneNumber(traveller.phone);
		const validated = regExp.filter(rule => !new RegExp(rule.pattern).test(cleanValue));

		if (!traveller.phone) {
			return i18n.t('Contacts:Required');
		}

		if (validated.length) {
			return i18n.t(`Contacts:${validated[0].error}`);
		}

		if (
			Object.keys(values).some(
				key =>
					values[key].selected &&
					values[key].id !== traveller.id &&
					clearPhoneNumber(values[key].phone) === cleanValue
			)
		) {
			return i18n.t(`Checkout:The phone numbers should not match`);
		}

		return undefined;
	};

	const validate = useCallback((values: SmsPopupFormValues): object => {
		const errors = {} as ValidationErrors;

		for (const key in values) {
			if (values[key].selected) {
				errors[key] = {};
				errors[key].phone = testPhone(values[key], values);
			}
		}

		return errors;
	}, []);

	return {
		validate
	};
};

export const useSmsPopupFormData = (
	travellers: (PassengerFragment | CheckinPassengerFragment)[],
	customerValues: TravellerParameterFragment[],
	smsServiceId: string
) => {
	const initialFormData = useMemo(() => {
		const initialFormData: SmsPopupFormValues = {};

		travellers.forEach(traveller => {
			const key = getFormTravellerKey(traveller.id);
			const selectedService = traveller.services.gdsServices?.services.find(
				service => service.serviceId === smsServiceId
			);
			const selected = !!selectedService;

			const phone = getValueByName(traveller.values, TravellerFields.Phone) || '';

			initialFormData[key] = {
				readOnly:
					selectedService?.products?.some(product => product.status === GdsServiceProductStatus.Ticketed) ||
					false,
				selected,
				phone: selected && phone ? formatPhone(phone) : '',
				id: traveller.id
			};
		});

		return initialFormData;
	}, [travellers]);

	const clearFormData = (form: FormApi<SmsPopupFormValues>) => {
		form.batch(() => {
			Object.keys(initialFormData).forEach(key => {
				if (!initialFormData[key].readOnly) {
					form.change(`${key}.phone`, '');
					form.change(`${key}.selected`, false);
				}
			});
		});
	};

	const isClearable = (values: SmsPopupFormValues) =>
		Object.values(values).some(field => field.selected && !field.readOnly);

	const travellersPhoneOptions = useMemo((): string[] => {
		const phoneSet = new Set<string>();

		const customerPhone = getUserValue({ values: customerValues }, TravellerFieldEnum.Phone);
		if (customerPhone) {
			phoneSet.add(customerPhone);
		}

		travellers.forEach(traveller => {
			const travellerPhone = getUserValue(traveller, TravellerFieldEnum.Phone);

			if (travellerPhone) {
				phoneSet.add(travellerPhone);
			}
		});

		return [...phoneSet].map(clearPhoneNumber);
	}, [travellers, customerValues]);

	return {
		initialFormData,
		clearFormData,
		isClearable,
		travellersPhoneOptions
	};
};
